import { useCallback, useEffect, useState } from 'react'
import type { AdminSchemas } from '@smorgrav/hundehall_shared'
import { apiFetch } from '@/api'

/**
 * The minimum app version per site, and the app releases to choose it from.
 * Writes go through PUT /admin/app-version, which only accepts a release that
 * deployed the app (or '' to clear).
 */
const useAppVersion = () => {
  const [data, setData] = useState<AdminSchemas.AppVersionResponse | null>(
    null
  )
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const refetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await apiFetch('/admin/app-version')
      if (!res.ok) {
        const body = await res.text().catch(() => '')
        throw new Error(`HTTP ${res.status}: ${body}`)
      }
      setData((await res.json()) as AdminSchemas.AppVersionResponse)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refetch()
  }, [refetch])

  return { data, error, loading, refetch }
}

const saveMinAppVersion = async (
  minAppVersion: string,
  siteIds: 'all' | string[]
): Promise<AdminSchemas.AppVersionUpdateResponse> => {
  const res = await apiFetch('/admin/app-version', {
    method: 'PUT',
    body: JSON.stringify({ minAppVersion, siteIds })
  })
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      detail?: string
    } | null
    throw new Error(body?.detail || `HTTP ${res.status}`)
  }
  return (await res.json()) as AdminSchemas.AppVersionUpdateResponse
}

export { saveMinAppVersion, useAppVersion }
